/**
 * HexFilterBar — the board's filter row: labelled groups of single-choice chips.
 *
 * Chips rather than dropdowns, because on this page the OPTIONS are half the information.
 * "Alerting 3" tells the operator there are three cameras worth looking at before they
 * click anything; the same choice behind a select would hide that number until after they
 * had already decided to go looking for it. Every group is single-choice and always has a
 * selection (an "All" chip is how you clear one), so the row can never end up in a state
 * whose meaning has to be worked out from what ISN'T lit.
 *
 * THE ROW HAS TO ANNOUNCE ITSELF. Chips with tiny all-caps headings above them read as tabs,
 * a legend, or a row of counters — anything but controls that change what you're looking at,
 * and an operator who doesn't know they're filters doesn't know why part of the board is
 * grey. Three things fix that, and none of them is a chip:
 *
 *   • each group sits in its own tinted well with a full-word label ("Camera state", not
 *     "STATE") ABOVE its chips, so the label reads as the question and the chips as its
 *     answers — side by side they were just another pill in the row;
 *   • the result line spells out the CONSEQUENCE — "3 of 12 cameras match, the rest are
 *     dimmed" — which is the part the board itself can't say, because a dimmed hexagon looks
 *     like a quiet camera;
 *   • Clear appears the moment anything is filtered, so there is always a visible way back to
 *     the whole board. It resets every group to its own default, so no page code is needed.
 *
 * The row used to open with a funnel icon and the word "Filter" as a fourth signal; it was
 * dropped as redundant on a bar whose every group already asks its question in words. The
 * name it carried is kept as the container's aria-label, so a screen reader still reaches
 * these controls as a labelled group rather than three unrelated ones.
 */
import type { ReactNode } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

export interface FilterChip {
  key: string;
  label: string;
  /** How many cameras this chip would leave on the board. */
  count?: number;
  /** Tailwind background class for a leading dot — severity/state colour. */
  dot?: string;
  title?: string;
}

export interface FilterGroup {
  /** Heading, in full words — "Camera state", not "STATE". See the file header. */
  label: string;
  chips: FilterChip[];
  value: string;
  /**
   * The chip that means "not filtering by this". Defaults to the first, which is the "All"
   * chip in every group here. It is what Clear resets to, and what decides whether the group
   * counts as switched on.
   */
  defaultValue?: string;
  /**
   * What this group DOES to the cameras it excludes, for the result line — and they differ:
   * choosing a site rebuilds the board around that site's cameras (the others are gone),
   * while the state and alert filters leave every hexagon in place and grey the misses. The
   * sentence has to match, or it sends the reader looking for dimmed cameras that aren't
   * there. Defaults to 'dim'.
   */
  effect?: 'dim' | 'hide';
  onChange: (key: string) => void;
}

interface HexFilterBarProps {
  groups: FilterGroup[];
  /** How many cameras survive the current filters. Drawn with `totalCount` as the result line. */
  matchCount?: number;
  totalCount?: number;
  /** What the filters act on, for the result line. Plural — "cameras", "pins". */
  noun?: string;
  /** Anything extra to sit at the end of the row (the map board's own controls). */
  trailing?: ReactNode;
  className?: string;
}

const baseOf = (g: FilterGroup) => g.defaultValue ?? g.chips[0]?.key;

export default function HexFilterBar({
  groups,
  matchCount,
  totalCount,
  noun = 'cameras',
  trailing,
  className = '',
}: HexFilterBarProps) {
  const shown = groups.filter((g) => g.chips.length > 1);
  if (shown.length === 0) return null;

  /** Groups actually narrowing the board — what Clear clears, and what the count on it means. */
  const applied = shown.filter((g) => g.value !== baseOf(g));
  const clearAll = () => applied.forEach((g) => g.onChange(baseOf(g) as string));
  const showResult = totalCount != null && matchCount != null;

  const dims = applied.some((g) => (g.effect ?? 'dim') === 'dim');
  const hides = applied.some((g) => g.effect === 'hide');
  const fate = dims && hides ? 'hidden or dimmed' : hides ? 'hidden' : 'dimmed';

  return (
    <div
      role="group"
      aria-label="Filter the board"
      /* items-end so the result line sits on the chips' line, with only the group labels
         riding above it — a bar that aligned on centres would stagger the two against each
         other. */
      className={`flex flex-shrink-0 flex-wrap items-end gap-x-3 gap-y-2 rounded-lg border border-border bg-card px-2.5 py-2 sm:px-3 ${className}`}
    >
      {shown.map((group) => {
        const on = group.value !== baseOf(group);
        return (
          <div
            key={group.label}
            role="group"
            aria-label={`Filter by ${group.label.toLowerCase()}`}
            /* A FAINT well. It exists to bracket a label with its chips, and it used to be the
               same grey the selected chip now wears — which left the two within a shade of each
               other and nothing on the row visibly chosen. The grouping only needs a hint; the
               selection needs the contrast. */
            className="flex min-w-0 flex-col gap-1 rounded-lg bg-muted/30 px-2 py-1"
          >
            {/* The label darkens when its group is switched on — with three wells side by
                side, that is the fastest way to see WHICH filter is the one narrowing the
                board without reading every chip in every group. */}
            <span
              className={`px-0.5 text-[11px] font-semibold ${on ? 'text-foreground' : 'text-muted-foreground'}`}
            >
              {group.label}
            </span>
            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
              {group.chips.map((chip) => {
                const active = chip.key === group.value;
                return (
                  <button
                    key={chip.key}
                    type="button"
                    aria-pressed={active}
                    title={chip.title || chip.label}
                    onClick={() => group.onChange(chip.key)}
                    /* Selected is a LIGHT GREY chip — the same one the view toggle uses, for the
                       same reason it was right there.
                       Brand was wrong: `--brand` is a vermilion a few degrees off
                       `--destructive`, so on a board about critical alerts a chosen filter wore
                       almost the alarm colour. Ink fixed that and overshot — a black pill beside
                       an "Alerting 3" chip became the heaviest thing in a bar that is meant to
                       be scenery. White on the old grey well was the other overshoot: too close
                       to its own background to find. Grey on a nearly-white well is the one that
                       reads, and it borrows no colour that means anything else on this page. */
                    className={`flex max-w-[220px] items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors ${
                      active
                        ? 'border-border bg-muted font-semibold text-foreground shadow-sm'
                        : 'border-transparent font-medium text-muted-foreground hover:bg-muted/60 hover:text-foreground'
                    }`}
                  >
                    {/* The dot keeps its own colour when selected. It is the STATE's colour —
                        red for alerting, slate for offline — and blanking it to the chip's ink
                        threw away the one thing on the chip that says which state this is. */}
                    {chip.dot && <span aria-hidden className={`h-2 w-2 flex-none rounded-full ${chip.dot}`} />}
                    <span className="truncate">{chip.label}</span>
                    {chip.count != null && (
                      <span
                        className={`flex-none rounded-full px-1.5 text-[10px] font-semibold tabular-nums ${
                          active ? 'bg-card text-foreground' : 'bg-muted/70 text-muted-foreground'
                        }`}
                      >
                        {chip.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Pushed to the far end: the result of the choices, then the way out of them. */}
      <div className="ml-auto flex items-center gap-2 py-1">
        {showResult && (
          <span className="text-[12px] text-muted-foreground" aria-live="polite">
            {applied.length > 0 ? (
              <>
                <span className="font-semibold text-foreground tabular-nums">{matchCount}</span> of{' '}
                <span className="tabular-nums">{totalCount}</span> {noun} match — the rest are {fate}
              </>
            ) : (
              <>
                <span className="tabular-nums">{totalCount}</span> {noun}
              </>
            )}
          </span>
        )}
        {applied.length > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="flex items-center gap-1 rounded-lg border border-border px-2 py-1 text-[12px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            // `noun`, not "camera": this bar is now also the alert list's type filter,
            // where the things being hidden are alerts.
            title={`Show every ${noun} again`}
          >
            <XMarkIcon className="h-3.5 w-3.5" />
            Clear {applied.length > 1 ? `(${applied.length})` : ''}
          </button>
        )}
        {trailing}
      </div>
    </div>
  );
}
